import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Edit3, Loader2, Plus, RefreshCcw, ShieldOff, X } from 'lucide-react';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AdminPromotion,
  AdminPromotionInput,
  closeAdminPromotion,
  createAdminPromotion,
  fetchAdminPromotions,
  updateAdminPromotion,
} from '@/services/adminService';
import { useToast } from '@/hooks/use-toast';
import { logging } from '@/services/logging';

type FormState = {
  name: string;
  description: string;
  code: string;
  discountType: 'PERCENT' | 'FIXED_AMOUNT';
  discountValue: string;
  currency: string;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
  stackable: boolean;
  usageLimitTotal: string;
  usageLimitPerUser: string;
  rules: string;
};

const defaultRules = JSON.stringify({
  scope: 'order',
  excludesShipment: true,
  audience: { type: 'all' },
}, null, 2);

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  code: '',
  discountType: 'PERCENT',
  discountValue: '',
  currency: 'USD',
  startsAt: '',
  endsAt: '',
  isActive: true,
  stackable: false,
  usageLimitTotal: '',
  usageLimitPerUser: '',
  rules: defaultRules,
});

const toDatetimeLocal = (value: string | null): string => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

const fromPromotion = (promotion: AdminPromotion): FormState => ({
  name: promotion.name,
  description: promotion.description ?? '',
  code: promotion.code ?? '',
  discountType: promotion.discountType,
  discountValue: promotion.discountValue != null ? String(promotion.discountValue) : '',
  currency: promotion.currency ?? 'USD',
  startsAt: toDatetimeLocal(promotion.startsAt),
  endsAt: toDatetimeLocal(promotion.endsAt),
  isActive: promotion.isActive,
  stackable: promotion.stackable,
  usageLimitTotal: promotion.usageLimitTotal != null ? String(promotion.usageLimitTotal) : '',
  usageLimitPerUser: promotion.usageLimitPerUser != null ? String(promotion.usageLimitPerUser) : '',
  rules: JSON.stringify(promotion.rules ?? { scope: 'order', excludesShipment: true, audience: { type: 'all' } }, null, 2),
});

const toIsoOrNull = (value: string): string | null => {
  if (!value.trim()) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new Error('Invalid date');
  }
  return date.toISOString();
};

const toOptionalInt = (value: string): number | null => {
  if (!value.trim()) {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 0) {
    throw new Error('Usage limits must be non-negative integers');
  }
  return parsed;
};

const buildPayload = (form: FormState): AdminPromotionInput => {
  const discountValue = Number(form.discountValue);
  if (!form.name.trim()) {
    throw new Error('Name is required');
  }
  if (!Number.isFinite(discountValue) || discountValue < 0) {
    throw new Error('Discount value must be a non-negative number');
  }

  let rules: unknown;
  try {
    rules = JSON.parse(form.rules || '{}');
  } catch {
    throw new Error('Rules must be valid JSON');
  }

  return {
    name: form.name.trim(),
    description: form.description.trim() || null,
    code: form.code.trim() || null,
    discountType: form.discountType,
    discountValue,
    currency: form.currency.trim().toUpperCase() || null,
    startsAt: toIsoOrNull(form.startsAt),
    endsAt: toIsoOrNull(form.endsAt),
    isActive: form.isActive,
    stackable: form.stackable,
    usageLimitTotal: toOptionalInt(form.usageLimitTotal),
    usageLimitPerUser: toOptionalInt(form.usageLimitPerUser),
    rules,
  };
};

const formatMoney = (value: number): string =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value);

const formatDate = (value: string | null): string => {
  if (!value) {
    return '-';
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const AdminPromotionsPage = () => {
  const { toast } = useToast();
  const [promotions, setPromotions] = useState<ReadonlyArray<AdminPromotion>>([]);
  const [form, setForm] = useState<FormState>(() => emptyForm());
  const [editing, setEditing] = useState<AdminPromotion | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPromotions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setPromotions(await fetchAdminPromotions());
    } catch (loadError) {
      logging.error('Failed to fetch admin promotions', { error: loadError });
      setError('Failed to load promotions');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPromotions();
  }, [loadPromotions]);

  const totals = useMemo(() => {
    return promotions.reduce(
      (acc, promotion) => ({
        count: acc.count + promotion.usageCount,
        discount: acc.discount + promotion.totalDiscountUsd,
        active: acc.active + (promotion.isActive ? 1 : 0),
      }),
      { count: 0, discount: 0, active: 0 },
    );
  }, [promotions]);

  const resetForm = () => {
    setEditing(null);
    setForm(emptyForm());
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const payload = buildPayload(form);
      if (editing) {
        await updateAdminPromotion(editing.id, payload);
        toast({ title: 'Promotion updated' });
      } else {
        await createAdminPromotion(payload);
        toast({ title: 'Promotion created' });
      }
      resetForm();
      await loadPromotions();
    } catch (saveError) {
      const responseMessage = isAxiosError(saveError) ? saveError.response?.data?.message : null;
      const message = typeof responseMessage === 'string'
        ? responseMessage
        : saveError instanceof Error
          ? saveError.message
          : 'Failed to save promotion';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = async (promotion: AdminPromotion) => {
    setError(null);
    try {
      await closeAdminPromotion(promotion.id);
      toast({ title: 'Promotion closed' });
      await loadPromotions();
    } catch (closeError) {
      logging.error('Failed to close promotion', { error: closeError, promotionId: promotion.id });
      setError('Failed to close promotion');
    }
  };

  const handleValidateRules = () => {
    setError(null);
    try {
      const parsed = JSON.parse(form.rules || '{}');
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Rules must be a JSON object');
      }

      const rules = parsed as Record<string, unknown>;
      const supportedKeys = new Set(['scope', 'excludesShipment', 'audience']);
      for (const key of Object.keys(rules)) {
        if (!supportedKeys.has(key)) {
          throw new Error(`Unsupported rules key: ${key}`);
        }
      }
      if (rules.scope != null && rules.scope !== 'order') {
        throw new Error('Only scope "order" is supported right now');
      }
      if (rules.excludesShipment != null && typeof rules.excludesShipment !== 'boolean') {
        throw new Error('excludesShipment must be true or false');
      }
      if (rules.audience != null) {
        if (typeof rules.audience !== 'object' || Array.isArray(rules.audience)) {
          throw new Error('audience must be an object');
        }
        const audience = rules.audience as Record<string, unknown>;
        const supportedAudienceKeys = new Set(['type', 'emails']);
        for (const key of Object.keys(audience)) {
          if (!supportedAudienceKeys.has(key)) {
            throw new Error(`Unsupported audience key: ${key}`);
          }
        }
        if (audience.type !== 'all' && audience.type !== 'specific_users') {
          throw new Error('audience.type must be "all" or "specific_users"');
        }
        if (audience.type === 'specific_users') {
          if (!Array.isArray(audience.emails) || audience.emails.length === 0) {
            throw new Error('audience.emails must contain at least one email');
          }
          const invalidEmail = audience.emails.find((entry) =>
            typeof entry !== 'string' || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(entry.trim()),
          );
          if (invalidEmail) {
            throw new Error('Every audience email must be a valid email string');
          }
        }
      }

      toast({ title: 'Rules JSON is valid' });
    } catch (validationError) {
      setError(validationError instanceof Error ? validationError.message : 'Rules JSON is invalid');
    }
  };

  return (
    <main className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-normal">Promotions</h1>
          <p className="text-sm text-muted-foreground">Create promo codes, close old promotions, and monitor redemption usage.</p>
        </div>
        <Button type="button" variant="outline" onClick={() => void loadPromotions()} disabled={isLoading}>
          {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active promotions</CardDescription>
            <CardTitle>{totals.active}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total redemptions</CardDescription>
            <CardTitle>{totals.count}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total promo discount</CardDescription>
            <CardTitle>{formatMoney(totals.discount)}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{editing ? 'Edit promotion' : 'New promotion'}</CardTitle>
          <CardDescription>Rules default to order-level discount excluding shipment.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="promotion-name">Name</Label>
              <Input id="promotion-name" value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="promotion-code">Promo code</Label>
              <Input id="promotion-code" value={form.code} className="uppercase" onChange={(event) => setForm((prev) => ({ ...prev, code: event.target.value.toUpperCase() }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="promotion-type">Discount type</Label>
              <Select value={form.discountType} onValueChange={(value) => setForm((prev) => ({ ...prev, discountType: value as FormState['discountType'] }))}>
                <SelectTrigger id="promotion-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PERCENT">Percent</SelectItem>
                  <SelectItem value="FIXED_AMOUNT">Fixed amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="promotion-value">Value</Label>
              <Input id="promotion-value" type="number" min="0" step="0.01" value={form.discountValue} onChange={(event) => setForm((prev) => ({ ...prev, discountValue: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="promotion-currency">Currency</Label>
              <Input id="promotion-currency" value={form.currency} onChange={(event) => setForm((prev) => ({ ...prev, currency: event.target.value.toUpperCase() }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="promotion-starts">Starts at</Label>
              <Input id="promotion-starts" type="datetime-local" value={form.startsAt} onChange={(event) => setForm((prev) => ({ ...prev, startsAt: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="promotion-ends">Ends at</Label>
              <Input id="promotion-ends" type="datetime-local" value={form.endsAt} onChange={(event) => setForm((prev) => ({ ...prev, endsAt: event.target.value }))} />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="promotion-total-limit">Total usage limit</Label>
              <Input id="promotion-total-limit" type="number" min="0" value={form.usageLimitTotal} onChange={(event) => setForm((prev) => ({ ...prev, usageLimitTotal: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="promotion-user-limit">Per-user limit</Label>
              <Input id="promotion-user-limit" type="number" min="0" value={form.usageLimitPerUser} onChange={(event) => setForm((prev) => ({ ...prev, usageLimitPerUser: event.target.value }))} />
            </div>
            <div className="flex items-center gap-3 pt-7">
              <Switch checked={form.stackable} onCheckedChange={(checked) => setForm((prev) => ({ ...prev, stackable: checked }))} />
              <Label>Stackable</Label>
            </div>
            <div className="flex items-center gap-3 pt-7">
              <Switch checked={form.isActive} onCheckedChange={(checked) => setForm((prev) => ({ ...prev, isActive: checked }))} />
              <Label>Active</Label>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="promotion-description">Description</Label>
            <Input id="promotion-description" value={form.description} onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="promotion-rules">Rules JSON</Label>
            <div className="space-y-1 text-sm text-muted-foreground">
              <p>
                Supported top-level keys: <span className="font-mono">scope</span>, <span className="font-mono">excludesShipment</span>, <span className="font-mono">audience</span>.
                <span className="font-mono"> scope</span> must be <span className="font-mono">"order"</span>.
                <span className="font-mono"> excludesShipment</span> must be <span className="font-mono">true</span> or <span className="font-mono">false</span>.
              </p>
              <p>
                Audience options: <span className="font-mono">{'{"type":"all"}'}</span> or <span className="font-mono">{'{"type":"specific_users","emails":["client@example.com"]}'}</span>.
                Emails are checked case-insensitively.
              </p>
              <p>
                Example: <span className="font-mono">{'{"scope":"order","excludesShipment":true,"audience":{"type":"specific_users","emails":["client@example.com"]}}'}</span>.
                Paper/item-level rules are not active yet.
              </p>
            </div>
            <textarea
              id="promotion-rules"
              value={form.rules}
              onChange={(event) => setForm((prev) => ({ ...prev, rules: event.target.value }))}
              className="min-h-28 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
            />
            <Button type="button" variant="outline" size="sm" onClick={handleValidateRules}>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Check JSON
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={() => void handleSave()} disabled={isSaving}>
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              {editing ? 'Save promotion' : 'Create promotion'}
            </Button>
            {editing ? (
              <Button type="button" variant="outline" onClick={resetForm}>
                <X className="mr-2 h-4 w-4" />
                Cancel edit
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Promotion usage</CardTitle>
          <CardDescription>Usage count and discount totals come from promo redemptions.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Promotion</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Limits</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Total discount</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="py-8 text-center text-muted-foreground">
                      <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                      Loading promotions
                    </TableCell>
                  </TableRow>
                ) : promotions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="py-8 text-center text-muted-foreground">No promotions yet</TableCell>
                  </TableRow>
                ) : promotions.map((promotion) => (
                  <TableRow key={promotion.id}>
                    <TableCell>
                      <div className="font-medium">{promotion.name}</div>
                      <div className="text-xs text-muted-foreground">{promotion.description || '-'}</div>
                    </TableCell>
                    <TableCell className="font-mono">{promotion.codeNorm || '-'}</TableCell>
                    <TableCell>
                      {promotion.discountType === 'PERCENT'
                        ? `${promotion.discountValue ?? 0}%`
                        : formatMoney(promotion.discountValue ?? 0)}
                    </TableCell>
                    <TableCell>
                      <span className={promotion.isActive ? 'text-emerald-600' : 'text-muted-foreground'}>
                        {promotion.isActive ? 'Active' : 'Closed'}
                      </span>
                      <div className="text-xs text-muted-foreground">{promotion.stackable ? 'Stackable' : 'Not stackable'}</div>
                    </TableCell>
                    <TableCell>
                      <div>Total: {promotion.usageLimitTotal ?? '-'}</div>
                      <div className="text-xs text-muted-foreground">Per user: {promotion.usageLimitPerUser ?? '-'}</div>
                    </TableCell>
                    <TableCell>{promotion.usageCount}</TableCell>
                    <TableCell>{formatMoney(promotion.totalDiscountUsd)}</TableCell>
                    <TableCell>{formatDate(promotion.lastRedeemedAt)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => {
                          setEditing(promotion);
                          setForm(fromPromotion(promotion));
                        }}>
                          <Edit3 className="mr-2 h-4 w-4" />
                          Edit
                        </Button>
                        <Button type="button" variant="outline" size="sm" disabled={!promotion.isActive} onClick={() => void handleClose(promotion)}>
                          <ShieldOff className="mr-2 h-4 w-4" />
                          Close
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </main>
  );
};

export default AdminPromotionsPage;
